import { Dialog } from '@headlessui/react';
import {
  BatteryFull,
  Search,
  ShieldCheck,
  Sparkle,
  Undo2,
  X,
} from 'lucide-react';
import Image from 'next/image';
import i18n from '@/settings/i18n';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import Modal from '@/components/v2/common/Modal';
import { localizeWarrantyDuration } from './helpers';

const t = i18n.init('product');

const data = (warranty: string, freeReturnDays: number) => [
  {
    icon: <Search className="h-5 w-5" />,
    title: t`Certified Quality`,
    text: t`Our experts verify each item's authenticity, functionality, and condition to ensure you receive a product you'll love.`,
  },
  {
    icon: <Undo2 className="h-5 w-5" />,
    title: t`Free Returns Up To ${{ freeReturnDays }} Days`,
    text: t`Changed your mind or not happy with the product you received? Return your purchase for any reason for up to ${{
      freeReturnDays,
    }} days. We'll provide a prepaid return label free of charge.`,
  },
  {
    icon: <ShieldCheck className="h-5 w-5" />,
    title: t`${{
      warranty: localizeWarrantyDuration(
        warranty,
        warranty.split(' ').join('-'),
      ),
    }} Warranty`,
    text: t`In the unlikely event of a functional issue, Reebelo's warranty has you covered for up to ${{
      warranty: localizeWarrantyDuration(warranty, warranty),
    }}. We'll repair or replace your product at no cost, with free shipping.`,
  },
  {
    icon: <BatteryFull className="h-5 w-5" />,
    title: t`Battery Health Guarantee`,
    text: t`On applicable products, all batteries come with at least 80% capacity for reliable daily use, and we often offer options to upgrade to 90% or 100%—these capacities are guaranteed. You're covered for 12 months against battery failure and overheating, and in the unlikely event that your battery drains within the first 3 months more than 10% below the battery tier you selected, you can return your device for a full refund. After the coverage period, we'll connect you with trusted repair partners for discounted battery replacements.`,
  },
  {
    icon: <Sparkle className="h-5 w-5" />,
    title: t`Wiped And Sanitized`,
    text: t`Professionally reset to factory settings on applicable products. All products are thoroughly cleaned, and sanitized. No leftover data, no dust, no surprises.`,
  },
];

const Header = ({
  hideModal,
  warranty,
}: {
  hideModal: () => void;
  warranty: string;
}) => (
  <Dialog.Title as="div" className="w-full bg-[#92F7FA] text-sm text-gray-700">
    <div className="relative flex min-h-[175px] w-full items-center justify-center">
      <div className="absolute left-0 bottom-0 h-full w-full">
        <Image
          src={getEdgeUrl(
            'images/collections/product-page/usps/usps-banner.png',
          )}
          height={428}
          width={900}
          alt="Sustainable Banner"
          className="h-full w-full object-cover object-left opacity-30 xxs:object-center xxs:opacity-100"
          quality={100}
        />
      </div>

      <div className="relative flex h-full w-full max-w-[300px] flex-col items-center justify-center p-5 text-center sm:max-w-[320px]">
        <h2 className="text-lg font-bold leading-tight sm:text-xl">
          {t`Verified Refurbished`}
        </h2>
        <p className="mt-2 leading-snug">
          {t`Every device is quality-tested, data-wiped, professionally cleaned, and backed by our warranty for up to ${{
            warranty: localizeWarrantyDuration(warranty, warranty),
          }}.`}
        </p>
      </div>
    </div>
    <button
      onClick={hideModal}
      type="button"
      className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-700 text-white outline-none transition duration-150 ease-in-out hover:scale-110 focus:outline-none"
    >
      <X className="h-4 w-4" />
    </button>
  </Dialog.Title>
);

const Content = ({
  warranty,
  freeReturnDays,
}: {
  warranty: string;
  freeReturnDays: number;
}) => (
  <div className="flex h-full flex-col justify-between p-5 text-sm">
    <div className="mt-5 space-y-10 first:mt-0">
      {data(warranty, freeReturnDays).map((item) => (
        <div className="flex items-start gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-700 text-white">
            {item.icon}
          </div>
          <div>
            <h2 className="text-base font-bold leading-none">
              {item.title}
            </h2>
            <p className="mt-1 leading-snug">{item.text}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

type UspsModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  warranty: string;
  freeReturnDays: number;
};

export default function UspsModal({
  isOpen,
  setIsOpen,
  warranty,
  freeReturnDays,
}: UspsModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <Header hideModal={() => setIsOpen(false)} warranty={warranty} />
      <Content warranty={warranty} freeReturnDays={freeReturnDays} />
    </Modal>
  );
}
